import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { BookingCreateData } from "@/services/types/api";
import { customerApi } from "@/services/api/customers";

// Extended booking interface for customer-specific data
export interface CustomerBooking {
  _id: string;
  propertyId: string;
  agentId: string;
  customerId: string;
  date: string;
  time: string;
  duration: number;
  status: "pending" | "confirmed" | "completed" | "cancelled" | "rescheduled";
  notes?: string;
  customerNotes?: string;
  createdAt: string;
  updatedAt: string;
  property: {
    _id: string;
    title: string;
    address: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
    };
    image: string;
    images?: string[];
    price: number;
  };
  agent: {
    _id: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    avatar?: string;
  };
  customer: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
  rescheduleHistory?: {
    _id: string;
    previousDate: string;
    previousTime: string;
    newDate: string;
    newTime: string;
    reason: string;
    timestamp: string;
  }[];
}

export interface BookingFilters {
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  propertyType?: string;
}

export interface BookingSortOptions {
  sortBy: "date" | "status" | "property";
  sortOrder: "asc" | "desc";
}

interface CustomerBookingsState {
  bookings: CustomerBooking[];
  upcomingBookings: CustomerBooking[];
  pastBookings: CustomerBooking[];
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  error: string | null;
  filters: BookingFilters;
  sortOptions: BookingSortOptions;
  lastUpdated: string | null;
}

const initialState: CustomerBookingsState = {
  bookings: [],
  upcomingBookings: [],
  pastBookings: [],
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  error: null,
  filters: {},
  sortOptions: {
    sortBy: "date",
    sortOrder: "desc",
  },
  lastUpdated: null,
};

// Mock booking data (kept for reference)
/*
const mockBookings: CustomerBooking[] = [
  {
    id: "booking_1",
    propertyId: "prop_1",
    agentId: "agent_1",
    customerId: "customer_1",
    date: "2024-02-15",
    time: "14:00",
    duration: 60,
    status: "confirmed",
    notes: "Customer is interested in the modern kitchen and balcony view.",
    customerNotes: "Please show me the storage space and parking availability.",
    createdAt: "2024-01-20T10:30:00Z",
    updatedAt: "2024-01-25T14:20:00Z",
    customer: {
      id: "customer_1",
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      phone: "+1 (555) 123-4567",
    },
    property: {
      id: "prop_1",
      title: "Modern Downtown Apartment",
      address: "123 Main St, New York, NY 10001",
      image: "/images/properties/prop_1.jpg",
      price: 850000,
    },
    agent: {
      id: "agent_1",
      firstName: "Sarah",
      lastName: "Johnson",
      phone: "+1 (555) 123-4567",
      email: "sarah.johnson@alkinarealty.com",
      avatar: "/images/agents/sarah.jpg",
    },
    rescheduleHistory: [
      {
        id: "reschedule_1",
        previousDate: "2024-02-10",
        previousTime: "10:00",
        newDate: "2024-02-15",
        newTime: "14:00",
        reason: "Customer requested different time",
        timestamp: "2024-01-22T09:15:00Z",
      },
    ],
  },
  {
    id: "booking_2",
    propertyId: "prop_2",
    agentId: "agent_2",
    customerId: "customer_1",
    date: "2024-02-20",
    time: "11:00",
    duration: 45,
    status: "pending",
    notes: "",
    customerNotes: "First time viewing this property. Very excited!",
    createdAt: "2024-01-28T16:45:00Z",
    updatedAt: "2024-01-28T16:45:00Z",
    customer: {
      id: "customer_1",
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      phone: "+1 (555) 123-4567",
    },
    property: {
      id: "prop_2",
      title: "Luxury Condo with City View",
      address: "456 Park Ave, New York, NY 10002",
      image: "/images/properties/prop_2.jpg",
      price: 1200000,
    },
    agent: {
      id: "agent_2",
      firstName: "Michael",
      lastName: "Chen",
      phone: "+1 (555) 987-6543",
      email: "michael.chen@alkinarealty.com",
      avatar: "/images/agents/michael.jpg",
    },
  },
  {
    id: "booking_3",
    propertyId: "prop_3",
    agentId: "agent_1",
    customerId: "customer_1",
    date: "2024-01-10",
    time: "15:30",
    duration: 60,
    status: "completed",
    notes: "Customer was very interested. Follow-up scheduled for next week.",
    customerNotes: "Great property, but need to discuss financing options.",
    createdAt: "2024-01-05T12:20:00Z",
    updatedAt: "2024-01-10T16:30:00Z",
    customer: {
      id: "customer_1",
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      phone: "+1 (555) 123-4567",
    },
    property: {
      id: "prop_3",
      title: "Cozy Family Home",
      address: "789 Oak St, Brooklyn, NY 11201",
      image: "/images/properties/prop_3.jpg",
      price: 750000,
    },
    agent: {
      id: "agent_1",
      firstName: "Sarah",
      lastName: "Johnson",
      phone: "+1 (555) 123-4567",
      email: "sarah.johnson@alkinarealty.com",
      avatar: "/images/agents/sarah.jpg",
    },
  },
];
*/

// Async thunks
export const fetchCustomerBookings = createAsyncThunk(
  "customerBookings/fetchCustomerBookings",
  async (
    params: { filters?: BookingFilters; sortOptions?: BookingSortOptions } = {},
    { rejectWithValue }
  ) => {
    try {
      const apiParams = {
        page: 1,
        limit: 50,
        ...params.filters,
        sortBy: params.sortOptions?.sortBy || "date",
        sortOrder: params.sortOptions?.sortOrder || "desc",
      };

      const response = await customerApi.getBookings(apiParams);
      return response.bookings;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch customer bookings"
      );
    }
  }
);

export const createBooking = createAsyncThunk(
  "customerBookings/createBooking",
  async (bookingData: BookingCreateData, { rejectWithValue }) => {
    try {
      const newBooking = await customerApi.createBooking(bookingData);
      return newBooking;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to create booking"
      );
    }
  }
);

export const updateBooking = createAsyncThunk(
  "customerBookings/updateBooking",
  async (
    { id, updates }: { id: string; updates: Partial<CustomerBooking> },
    { rejectWithValue }
  ) => {
    try {
      const updatedBooking = await customerApi.updateBooking(id, updates);
      return { id, updates: updatedBooking };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to update booking"
      );
    }
  }
);

export const cancelBooking = createAsyncThunk(
  "customerBookings/cancelBooking",
  async (
    { id, reason }: { id: string; reason?: string },
    { rejectWithValue }
  ) => {
    try {
      const cancelledBooking = await customerApi.cancelBooking(id, reason);
      return { id, reason, booking: cancelledBooking };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to cancel booking"
      );
    }
  }
);

export const rescheduleBooking = createAsyncThunk(
  "customerBookings/rescheduleBooking",
  async (
    {
      id,
      newDate,
      newTime,
      reason,
    }: { id: string; newDate: string; newTime: string; reason?: string },
    { rejectWithValue }
  ) => {
    try {
      const rescheduledBooking = await customerApi.rescheduleBooking(id, {
        newDate,
        newTime,
        reason,
      });
      return { id, newDate, newTime, reason, booking: rescheduledBooking };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to reschedule booking"
      );
    }
  }
);

const customerBookingsSlice = createSlice({
  name: "customerBookings",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearBookings: (state) => {
      state.bookings = [];
      state.upcomingBookings = [];
      state.pastBookings = [];
      state.lastUpdated = null;
    },
    setFilters: (state, action: PayloadAction<BookingFilters>) => {
      state.filters = action.payload;
    },
    setSortOptions: (state, action: PayloadAction<BookingSortOptions>) => {
      state.sortOptions = action.payload;
    },
    updateBookingField: (
      state,
      action: PayloadAction<{
        id: string;
        field: keyof CustomerBooking;
        value: any;
      }>
    ) => {
      const booking = state.bookings.find((b) => b._id === action.payload.id);
      if (booking) {
        (booking as any)[action.payload.field] = action.payload.value;
        booking.updatedAt = new Date().toISOString();
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch customer bookings
      .addCase(fetchCustomerBookings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerBookings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bookings = action.payload;

        // Separate upcoming and past bookings
        const now = new Date();
        state.upcomingBookings = action.payload.filter(
          (booking: any) =>
            new Date(booking.date) >= now &&
            booking.status !== "cancelled" &&
            booking.status !== "completed"
        );
        state.pastBookings = action.payload.filter(
          (booking: any) =>
            new Date(booking.date) < now ||
            booking.status === "cancelled" ||
            booking.status === "completed"
        );

        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchCustomerBookings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Create booking
      .addCase(createBooking.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createBooking.fulfilled, (state, action) => {
        state.isCreating = false;
        state.bookings.unshift(action.payload);

        // Update upcoming bookings
        const now = new Date();
        if (new Date(action.payload.date) >= now) {
          state.upcomingBookings.unshift(action.payload);
        }

        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(createBooking.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })

      // Update booking
      .addCase(updateBooking.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateBooking.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.bookings.findIndex(
          (booking) => booking._id === action.payload.id
        );
        if (index !== -1) {
          state.bookings[index] = {
            ...state.bookings[index],
            ...action.payload.updates,
          };
        }
        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(updateBooking.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })

      // Cancel booking
      .addCase(cancelBooking.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(cancelBooking.fulfilled, (state, action) => {
        state.isUpdating = false;
        const booking = state.bookings.find((b) => b._id === action.payload.id);
        if (booking) {
          booking.status = "cancelled";
          booking.updatedAt = new Date().toISOString();
        }
        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(cancelBooking.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })

      // Reschedule booking
      .addCase(rescheduleBooking.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(rescheduleBooking.fulfilled, (state, action) => {
        state.isUpdating = false;
        const booking = state.bookings.find((b) => b._id === action.payload.id);
        if (booking) {
          const previousDate = booking.date;
          const previousTime = booking.time;

          booking.date = action.payload.newDate;
          booking.time = action.payload.newTime;
          booking.status = "rescheduled";
          booking.updatedAt = new Date().toISOString();

          // Add to reschedule history
          if (!booking.rescheduleHistory) {
            booking.rescheduleHistory = [];
          }
          booking.rescheduleHistory.unshift({
            _id: `reschedule_${Date.now()}`,
            previousDate,
            previousTime,
            newDate: action.payload.newDate,
            newTime: action.payload.newTime,
            reason: action.payload.reason || "No reason provided",
            timestamp: new Date().toISOString(),
          });
        }
        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(rescheduleBooking.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearBookings,
  setFilters,
  setSortOptions,
  updateBookingField,
} = customerBookingsSlice.actions;

export default customerBookingsSlice.reducer;
